await import(`@liferay/language/${Liferay.ThemeDisplay.getLanguageId()}/object-js-components-web/all.js`);
/**
 * SPDX-FileCopyrightText: (c) 2000 Liferay, Inc. https://liferay.com
 * SPDX-License-Identifier: LGPL-2.1-or-later OR LicenseRef-Liferay-DXP-EULA-2.0.0-2023-06
 */

import ClayButton, {ClayButtonWithIcon} from '@clayui/button';
import ClayEmptyState from '@clayui/empty-state';
import ClayList from '@clayui/list';
import classNames from 'classnames';
import {ManagementToolbar} from 'frontend-js-components-web';
import React, {useMemo, useState} from 'react';
import {DndProvider} from 'react-dnd';
import {HTML5Backend} from 'react-dnd-html5-backend';

import {filterArrayByQuery} from '../../utils/array';
import {getLocalizableLabel} from '../../utils/string';
import {ManagementToolbarSearch} from '../ManagementToolbar/ManagementToolbarSearch';
import BuilderListItem from './BuilderListItem';

import './BuilderScreen.scss';

export function BuilderScreen({
	builderScreenItems,
	creationLanguageId,
	defaultSort,
	disableEdit,
	emptyState,
	filter,
	firstColumnHeader,
	hasDragAndDrop,
	onChangeColumnOrder,
	onDeleteColumn,
	onEditing,
	onEditingObjectFieldName,
	onVisibleEditModal,
	openModal,
	secondColumnHeader,
	thirdColumnHeader,
}: IProps) {
	const [query, setQuery] = useState('');

	const filteredItems = useMemo(() => {
		return filterArrayByQuery({
			array: builderScreenItems,
			query,
			str: 'fieldLabel',
		});
	}, [builderScreenItems, query]);

	const tableItems = query ? filteredItems : builderScreenItems;

	return (
		<>
			{tableItems.length ? (
				<ClayList>
					<div>
						<ClayList.Item className="lfr-object__object-builder-screen-management-bar">
							<ManagementToolbar.Container>
								<ManagementToolbar.ItemList expand>
									<ManagementToolbarSearch
										query={query}
										setQuery={setQuery}
									/>

									<ManagementToolbar.Item>
										<ClayButtonWithIcon
											aria-label={Liferay.Language.get(
												'add'
											)}
											className="lfr-object__object-builder-screen-management-bar-button"
											onClick={openModal}
											symbol="plus"
										/>
									</ManagementToolbar.Item>
								</ManagementToolbar.ItemList>
							</ManagementToolbar.Container>
						</ClayList.Item>

						{tableItems.map((viewColumn, index) => (
							<React.Fragment key={viewColumn?.objectFieldName}>
								{index === 0 && (
									<ClayList.Item flex>
										<ClayList.ItemField
											className={classNames(
												'lfr-object__object-builder-screen-first-column',
												!hasDragAndDrop &&
													'lfr-object__object-builder-screen-first-column--not-draggable'
											)}
											expand
										>
											{firstColumnHeader}
										</ClayList.ItemField>

										<ClayList.ItemField
											className={classNames(
												'lfr-object__object-builder-screen-second-column',
												!hasDragAndDrop &&
													'lfr-object__object-builder-screen-second-column--not-draggable'
											)}
											expand
										>
											<ClayList.ItemField>
												{secondColumnHeader}
											</ClayList.ItemField>
										</ClayList.ItemField>

										{thirdColumnHeader && (
											<ClayList.ItemField
												className={classNames(
													'lfr-object__object-builder-screen-third-column',
													!hasDragAndDrop &&
														'lfr-object__object-builder-screen-third-column--not-draggable'
												)}
												expand
											>
												<ClayList.ItemField>
													{thirdColumnHeader}
												</ClayList.ItemField>
											</ClayList.ItemField>
										)}
									</ClayList.Item>
								)}

								{

									// @ts-ignore

									<DndProvider backend={HTML5Backend}>
										<BuilderListItem
											disableEdit={
												disableEdit ||
												(filter &&
													viewColumn?.disableEdit)
											}
											hasDragAndDrop={hasDragAndDrop}
											index={index}
											label={viewColumn?.fieldLabel}
											objectFieldName={
												viewColumn.objectFieldName
											}
											onChangeColumnOrder={
												onChangeColumnOrder
											}
											onDeleteColumn={onDeleteColumn}
											onEditing={onEditing}
											onEditingObjectFieldName={
												onEditingObjectFieldName
											}
											onVisibleEditModal={
												onVisibleEditModal
											}
											secondColumnValue={
												defaultSort
													? viewColumn.sortOrder ===
														'asc'
														? Liferay.Language.get(
																'ascending'
															)
														: Liferay.Language.get(
																'descending'
															)
													: filter
														? viewColumn?.objectFieldBusinessType
														: getLocalizableLabel(
																creationLanguageId as Liferay.Language.Locale,
																viewColumn?.label,
																viewColumn.objectFieldName
															)
											}
											thirdColumnValues={
												viewColumn?.valueList ??
												viewColumn?.value
											}
										/>
									</DndProvider>
								}
							</React.Fragment>
						))}
					</div>
				</ClayList>
			) : query ? (
				<div className="lfr-object__object-builder-screen-empty-state">
					<ClayEmptyState
						description={Liferay.Language.get(
							'sorry,-no-results-were-found'
						)}
						title={Liferay.Language.get('no-results-found')}
					/>
				</div>
			) : (
				<div className="lfr-object__object-builder-screen-empty-state">
					<ClayEmptyState
						description={emptyState.description}
						title={emptyState.title}
					>
						<ClayButton displayType="secondary" onClick={openModal}>
							{emptyState.buttonText}
						</ClayButton>
					</ClayEmptyState>
				</div>
			)}
		</>
	);
}

type TLabelValueObject = {
	label: string;
	value: string;
};

export type TBuilderScreenItem = {
	defaultSort?: boolean;
	disableEdit?: boolean;
	externalReferenceCode?: string;
	fieldLabel?: string;
	filterBy?: string;
	label?: LocalizedValue<string>;
	objectFieldBusinessType?: string;
	objectFieldName: string;
	priority?: number;
	sortOrder?: string;
	type?: string;
	value?: string;
	valueList?: TLabelValueObject[];
};

interface IProps {
	builderScreenItems: TBuilderScreenItem[];
	creationLanguageId?: Liferay.Language.Locale;
	defaultSort?: boolean;
	disableEdit?: boolean;
	emptyState: {
		buttonText: string;
		description: string;
		title: string;
	};
	filter?: boolean;
	firstColumnHeader: string;
	hasDragAndDrop?: boolean;
	onChangeColumnOrder?: (draggedIndex: number, targetIndex: number) => void;
	onDeleteColumn: (objectFieldName: string) => void;
	onEditing?: (boolean: boolean) => void;
	onEditingObjectFieldName?: (objectFieldName: string) => void;
	onVisibleEditModal?: (boolean: boolean) => void;
	openModal: () => void;
	secondColumnHeader: string;
	thirdColumnHeader?: string;
}
